import React from 'react';

import {
  matchRoutes,
  Navigate,
  Outlet,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { SESSION_EXPIRED_ERROR } from '@/assets/AppConstants';

import { useAppContext } from '@/contexts/app';
import { useUserContext } from '@/contexts/user';
import { useAbilityContext } from '@/contexts/ability';
import { useWorkspaceContext } from '@/contexts/workspace';
import { useKeycloakContext } from '@/contexts/keycloak/context';

import { getHomeFromRole } from '@/utils/routes';
import { APP_ROUTES } from '@/assets/MenuItems';

import Forbidden from '@/views/base/Forbidden';
import ErrorBoundary from '@/views/base/ErrorBoundary';

import { LoadingLayout } from '../Loading';

export const AuthenticatedLayout = () => {
  const { workspace = 0 } = useParams();

  const location = useLocation();
  const [searchParams] = useSearchParams();

  const ability = useAbilityContext();
  const { requestStatus } = useAppContext();
  const { cleanKeycloak } = useKeycloakContext();
  const { userIsLogged } = useUserContext();
  const { workspace: { getWorkspaceRole } } = useWorkspaceContext();

  const finalLocation = location.pathname?.includes('logout')
    ? ''
    : location.pathname;

  if (!userIsLogged()) {
    const redirectTo = `/login?callbackUrl=${encodeURIComponent(
      finalLocation,
    )}`;
    return <Navigate to={redirectTo} replace />;
  }

  const isSessionExpired = requestStatus === SESSION_EXPIRED_ERROR;
  if (isSessionExpired) {
    cleanKeycloak();
    const redirectTo = `/login?callbackUrl=${encodeURIComponent(
      finalLocation,
    )}`;
    return <Navigate to={redirectTo} replace />;
  }

  if (!getWorkspaceRole()) {
    return <LoadingLayout />;
  }

  const menuMatchRoutes = matchRoutes(APP_ROUTES, location);

  if (!menuMatchRoutes) {
    const callbackUrl = searchParams.get('callbackUrl')
      || `/${workspace}${getHomeFromRole(getWorkspaceRole())}`
      || '/';

    return (
      <Navigate
        to={callbackUrl}
        replace
      />
    );
  }

  const { length, [length - 1]: routeItem } = menuMatchRoutes;
  const {
    params: { taskId: paramsTaskId },
    route: { key },
  } = routeItem;

  const keyId = paramsTaskId || key;
  const canView = ability.can('view', keyId);
  const isEnabled = ability.can('enabled', keyId);

  return (
    <>
      {canView && isEnabled && (
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      )}
      {
        (!canView || !isEnabled) && (
          <Forbidden />
        )
      }
    </>
  );
};
